/**
 * Which language a visitor gets when the URL does not say (docs/00-decisions.md — "Market, language").
 *
 * The rule is by country: Hebrew in Israel, English everywhere else. In order:
 *
 *   1. a language already chosen or used on this device (the `NEXT_LOCALE` cookie next-intl keeps);
 *   2. the country a CDN in front of us reports — Cloudflare, Vercel, CloudFront, or a header named
 *      in `GEO_COUNTRY_HEADER` — or, when there is no CDN, the country the browser worked out from
 *      its own time zone on an earlier visit (`freealbum.country`, see `LocaleGuess`);
 *   3. failing all that, the browser's language: Hebrew if it lists Hebrew, else English.
 *
 * Pure functions, no Next imports, so the proxy, the client component and the tests share them.
 */
import { locales, routing, type Locale } from './routing';

export type LocaleSource = 'cookie' | 'country' | 'language';

export interface LocaleDecision {
  locale: Locale;
  source: LocaleSource;
  /** ISO 3166-1 alpha-2 when the decision came from a country; `ZZ` = "somewhere that is not Israel" */
  country: string | null;
}

/** Headers set by the CDNs people actually put in front of a site, lowest-effort first. */
export const COUNTRY_HEADERS = ['cf-ipcountry', 'x-vercel-ip-country', 'cloudfront-viewer-country', 'x-country-code'] as const;

/** the country cookie the browser writes from its time zone; read here, written by `LocaleGuess` */
export const COUNTRY_COOKIE = 'freealbum.country';
/** how the last unprefixed request was decided, so the browser knows whether it may correct it */
export const LOCALE_SOURCE_COOKIE = 'freealbum.locale-source';
export const LOCALE_COOKIE = 'NEXT_LOCALE';
export const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

export const ISRAEL_TIME_ZONES: ReadonlySet<string> = new Set(['Asia/Jerusalem', 'Asia/Tel_Aviv', 'Israel']);

export const localeForCountry = (country: string): Locale => (country === 'IL' ? 'he' : 'en');

/** Upper-case two letters, or null. Cloudflare's `XX` (unknown) and `T1` (Tor) carry no country. */
export function normalizeCountry(value: string | null | undefined): string | null {
  const v = value?.trim().toUpperCase() ?? '';
  if (!/^[A-Z]{2}$/.test(v) || v === 'XX' || v === 'T1') return null;
  return v;
}

export function countryFromHeaders(header: (name: string) => string | null | undefined, customHeader?: string): string | null {
  const names = customHeader?.trim() ? [customHeader.trim().toLowerCase(), ...COUNTRY_HEADERS] : [...COUNTRY_HEADERS];
  for (const name of names) {
    const country = normalizeCountry(header(name));
    if (country) return country;
  }
  return null;
}

/** `IL` in Israel, `ZZ` anywhere else, null when the browser has no usable zone. */
export function countryFromTimeZone(timeZone: string | null | undefined): 'IL' | 'ZZ' | null {
  if (!timeZone) return null;
  return ISRAEL_TIME_ZONES.has(timeZone) ? 'IL' : 'ZZ';
}

/** True when any language the browser lists is Hebrew (`he`, or the legacy `iw`). */
export function listsHebrew(acceptLanguage: string | null | undefined): boolean {
  if (!acceptLanguage) return false;
  return acceptLanguage
    .split(',')
    .map((part) => part.split(';')[0]!.trim().toLowerCase())
    .some((tag) => tag === 'he' || tag === 'iw' || tag.startsWith('he-') || tag.startsWith('iw-'));
}

export function isLocale(value: string | null | undefined): value is Locale {
  return typeof value === 'string' && (locales as readonly string[]).includes(value);
}

export function decideLocale(input: {
  cookie: (name: string) => string | null | undefined;
  header: (name: string) => string | null | undefined;
  /** extra header name to consult first, from `GEO_COUNTRY_HEADER` */
  customCountryHeader?: string;
}): LocaleDecision {
  const chosen = input.cookie(LOCALE_COOKIE);
  if (isLocale(chosen)) return { locale: chosen, source: 'cookie', country: null };

  const country = countryFromHeaders(input.header, input.customCountryHeader) ?? normalizeCountry(input.cookie(COUNTRY_COOKIE));
  if (country) return { locale: localeForCountry(country), source: 'country', country };

  return { locale: listsHebrew(input.header('accept-language')) ? 'he' : 'en', source: 'language', country: null };
}

/** `/en`, `/en/...`, `/he/...` — a URL that already names its language is never redirected. */
export function hasLocalePrefix(pathname: string): boolean {
  return locales.some((l) => pathname === `/${l}` || pathname.startsWith(`/${l}/`));
}

export const defaultLocale: Locale = routing.defaultLocale;
